import { memo, useCallback } from 'react'
import { Question } from 'types'
import { FillBlankViewer } from 'lesson-builder/question-types/fill-blank'
import { MultipleChoiceViewer } from 'lesson-builder/question-types/multiple-choice'
import { ShortAnswerViewer } from 'lesson-builder/question-types/short-answer'
import { ViewActionRow } from 'lesson-builder/components/view-action-row'

/**
 * @module lesson-builder/components/question-content
 * @description Renders the appropriate question viewer for a question's
 * type, plus the view-mode action row. See
 * docs/BLUEPRINT-2026-10-01-question-slide-decomposition.md.
 */
const QUESTION_COMPONENTS = {
	'multiple-choice': MultipleChoiceViewer,
	'short-answer': ShortAnswerViewer,
	'fill-blank': FillBlankViewer
} as const;

export const QuestionContent = memo(({ question, setIsEditing, onApprove, onEdit, showFeedback, setShowFeedback, canEdit }: { question: Question, setIsEditing: (isEditing: boolean) => void, onApprove: (approved: boolean) => void, onEdit: (payload: Partial<Question>) => void, showFeedback: boolean, setShowFeedback: (showFeedback: boolean) => void, canEdit?: boolean }) => {
	const handleRemoveImage = useCallback((optionIndex?: number) => {
		if (optionIndex !== undefined) {
			const currentUrls = (question as any).optionImageUrls || {}
			const newUrls = { ...currentUrls }
			delete newUrls[optionIndex]
			onEdit({ optionImageUrls: newUrls } as Partial<Question>)
		} else {
			onEdit({ imageUrl: undefined } as Partial<Question>)
		}
	}, [question, onEdit])

	if (!question) {
		return (
			<p className="question-text">No question available for this difficulty level.</p>
		)
	}

	const QuestionComponent = QUESTION_COMPONENTS[question.type]

	const handleApprove = (approved: boolean) => {
		onApprove(approved)
	}

	if (!QuestionComponent) {
		return (
			<>
				<p className="question-text">{question.question}</p>
				<div className="question-answer">{question.answer}</div>
			</>
		)
	}

	return (
		<div>
			<QuestionComponent question={question} onRemoveImage={canEdit !== false ? handleRemoveImage : undefined} />
			<ViewActionRow
				isApproved={question.approved}
				isDisapproved={question.isDisapproved}
				handleApprove={handleApprove}
				setIsEditing={setIsEditing}
				setShowFeedback={setShowFeedback}
				canEdit={canEdit} />
		</div>
	)
})

export default QuestionContent
